import { useContext } from 'react';
import { contentPipeline, type Root } from '@doist/content-system';
import { initializeZendeskApi } from '@doist/help-center-utils';
import * as HelpCenter from '@doist/help-center-utils';
import { helpCenterRules } from '@doist/marketist';
import * as Sentry from '@sentry/nextjs';
import { StructuredDataMarkup } from 'components/blog-posts/structured-data-markup';
import { HelpCenterDraftModeBanner } from 'components/draft-mode/help-draft-mode-banner';
import { NotFound } from 'components/error/not-found';
import Footer from 'components/Footer/Footer';
import * as Sections from 'components/help/articles';
import Header from 'components/help/Header/Header';
import Seo from 'components/Seo/Seo';
import { getAllLanguages } from 'i18n/constants';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { HelpCenterRedirects } from 'server/redirects-help-center';
import BannersClient from 'services/contentful/clients/BannersClient';
import { getContent } from 'services/help-center/content';
import { extractFirstParagraph } from 'services/help-center/utils';
import { getOgImageURL } from 'services/ogimage';
import type { GetStaticPropsContext, Transifex } from 'types';
import type { PageBanner } from 'types/contentful';
import { UserContextV2 } from '@/app/services/providers/contexts/user-context';

/** Custom social images for certain articles. */
const ogimageArticleMap: {
  [key: number]: string;
} = {
  360007080399: 'og-image-whats-new.png'
};
type Props = {
  article: HelpCenter.Article;
  categories: HelpCenter.PartialCategory[];
  category?: HelpCenter.PartialCategory;
  section?: HelpCenter.PartialSection;
  relatedArticles: HelpCenter.PartialArticle[];
  pageBanner: PageBanner;
  notFound?: boolean;
  description?: string;
  draftMode: boolean;
};
export default function ArticlePage({
  categories,
  article,
  category,
  section,
  relatedArticles,
  pageBanner,
  notFound,
  description,
  draftMode
}: Props) {
  const {
    t,
    i18n
  } = useTranslation('helpcenter');
  const {
    user,
    isAdmin
  } = useContext(UserContextV2);
  if (notFound) return <NotFound />;
  const utm_campaign = 'helpcenter';
  const ogimage = getOgImageURL(ogimageArticleMap[article.id] ?? 'og-image-todoist-help-center.png', i18n.language);
  const languagePath = i18n.language === 'en' ? '' : `/${i18n.language}`;
  const breadcrumbs = [{
    name: t('menu.helpCenter'),
    item: `${process.env.NEXT_PUBLIC_BASE_URL}${languagePath}/help`
  }];
  if (category) {
    breadcrumbs.push({
      name: category.name,
      item: `${process.env.NEXT_PUBLIC_BASE_URL}${languagePath}${category.href}`
    });
  }
  if (section) {
    breadcrumbs.push({
      name: section.name,
      item: `${process.env.NEXT_PUBLIC_BASE_URL}${languagePath}${section.href}`
    });
  }
  return <>
            <Seo path={article.href} title={article.title} description={description || t('seo.description')} images={[{
      url: ogimage,
      width: 1200,
      height: 628,
      alt: t('seoImageAlt')
    }]} noindex={article.labels.includes('nosearch')} data-sentry-element="Seo" data-sentry-source-file="[articleId].tsx" />
            <StructuredDataMarkup type="Article" title={article.title} authors={[{
      type: 'Organization',
      name: t('theCustomerExperienceTeam'),
      url: 'https://todoist.com/help'
    }]} images={[ogimage]} datePublished={article.createdAt} dateModified={article.updatedAt || undefined} breadcrumb={breadcrumbs} data-sentry-element="StructuredDataMarkup" data-sentry-source-file="[articleId].tsx" />
            {draftMode ? <HelpCenterDraftModeBanner articleId={article.id} articleHref={article.href} /> : null}
            <Header data={{
      categories,
      user
    }} articleId={article.id} isAdmin={isAdmin} currentPath={article.href} searchBarEnabled mobileSearchBarEnabled showContactUs pageBanner={pageBanner} data-sentry-element="Header" data-sentry-source-file="[articleId].tsx" />
            <main>
                <Sections.HeadingSection user={user} article={article} category={category} section={section} data-sentry-element="unknown" data-sentry-source-file="[articleId].tsx" />
                <Sections.ArticleContent article={article} relatedArticles={relatedArticles} data-sentry-element="unknown" data-sentry-source-file="[articleId].tsx" />
                <Footer title={t('footerTitle')} utm_campaign={utm_campaign} variant="neutral" data-sentry-element="Footer" data-sentry-source-file="[articleId].tsx" />
            </main>
        </>;
}
export async function getStaticProps({
  params,
  draftMode
}: GetStaticPropsContext) {
  const slug = params?.articleId as string;
  const resolvedSlug = HelpCenterRedirects.has(slug) ? HelpCenterRedirects.get(slug) as string : slug;
  const id = HelpCenter.resolveArticleIdFromPath(resolvedSlug);
  const locale = params?.locale as Transifex.Languages;
  let article: HelpCenter.Article | null = null;
  const [content, englishContent, {
    _nextI18Next
  }, pageBanner] = await Promise.all([getContent(locale), getContent('en'), serverSideTranslations(locale, ['common', 'helpcenter']), BannersClient.getPageBanner('/help', {
    lang: locale,
    cacheEnabled: false
  })]);
  if (draftMode) {
    // load from zendesk
    // parse with help center utils
    article = await initializeZendeskApi(locale).getArticle(id);
    const transform = contentPipeline({
      source: 'html',
      rules: helpCenterRules
    });
    // TODOO: remove casting once the help center utils is updated to set the correct type.
    article.body = transform(article.body) as unknown as string;
  } else {
    if (!content || !englishContent) {
      /**
       * Sentry reports errors where article can't be found because content is undefined
       * Debugging requires more context to find the root cause
       * Attempting to gather that context
       *
       * @see: https://doist-projects.sentry.io/issues/5743277463/events/ac3db038428d4ea6915bb0018aabbf31/
       */
      Sentry.setContext('Article', {
        locale,
        id,
        slug,
        resolvedSlug
      });
    }
    article = HelpCenter.findArticle(id ?? slug, content);

    // Show English version
    if (!article && locale !== 'en') {
      article = HelpCenter.findArticle(id ?? slug, englishContent);
    }
    if (!article) {
      return {
        props: {
          _nextI18Next,
          notFound: true
        },
        revalidate: 3600
      };
    }

    // Redirect to canonical URL
    const url = new URL(article.href, process.env.NEXT_PUBLIC_BASE_URL);
    const parts = url.pathname.split('/');
    if (parts[parts.length - 1] !== slug) {
      return {
        redirect: {
          destination: url.toString(),
          permanent: false
        }
      };
    }
  }
  if (!article) {
    return {
      props: {
        _nextI18Next,
        notFound: true
      },
      revalidate: 3600
    };
  }
  const relatedArticles = HelpCenter.findArticles(article.labels.map((label: string) => {
    if (label.includes('related-')) {
      const idStr = label.replace(/related-/, '');
      const id = Number.parseInt(idStr, 10);
      if (!Number.isNaN(id)) {
        return id;
      }
    }
    return undefined;
  }).filter(Boolean) as number[], HelpCenter.getPartialArticles(content.articles));
  const categories = HelpCenter.getPartialCategories(content.categories);
  const {
    category,
    section
  } = HelpCenter.getCategoryAndSectionByArticleId(article.id, categories);
  const description = extractFirstParagraph(article.body as unknown as Root);
  return {
    props: {
      _nextI18Next,
      pageBanner,
      categories,
      category: category ?? null,
      section: section ?? null,
      description,
      article,
      relatedArticles,
      draftMode: Boolean(draftMode)
    },
    revalidate: 3600
  };
}
export async function getStaticPaths() {
  if (process.env.NEXT_PUBLIC_APP_ENV !== 'production') return {
    paths: [],
    fallback: 'blocking'
  };
  const locales = getAllLanguages();
  const paths: {
    params: {
      articleId: string;
      locale: string;
    };
  }[] = [];
  for (const locale of locales) {
    const content = await getContent(locale);
    paths.push(...content.articles.map(article => {
      const path = article.href.split('/').pop() as string;
      return {
        params: {
          articleId: path,
          locale
        }
      };
    }));
  }
  return {
    paths: paths,
    fallback: 'blocking'
  };
}